import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Badge } from "src/components/base/badge";

/**
 * The properties for {@link StatusBadge}
 */
export type StatusBadgeProps = {
    /** Additional classname to set on the badge */
    className?: string;

    /**
     * Hide the label on narrow (<lg) viewports, showing only the dot.
     * Useful in dense table cells where space is limited.
     */
    compact?: boolean;

    /** Whether the device is currently online. Defaults to `true`. */
    online?: boolean;

    /**
     * Last time the device was seen. Rendered as the badge's label when
     * offline; when omitted (or invalid) the "offline" label is shown instead.
     */
    lastSeen?: string | Date | null;
};

/**
 * Format an absolute date+time using the user's locale.
 *
 * @param value the date to format
 * @returns the formatted date+time, or `null` if `value` is empty/invalid
 */
function formatLastSeen(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/**
 * A badge indicating a device's connectivity status: a pulsing dot next to
 * the "online" label while online, otherwise a static dot next to the
 * device's last-seen date (or the "offline" label if unknown).
 */
export function StatusBadge(props: StatusBadgeProps) {
    const { className, compact, online = true, lastSeen } = props;
    const [tg] = useTranslation();

    const label = online ? tg("label.online") : (formatLastSeen(lastSeen) ?? tg("label.offline"));

    return (
        <Badge color={online ? "emerald" : "zinc"} className={className}>
            <span className={"pointer-events-none relative flex size-2"}>
                {online && (
                    <span
                        className={
                            "absolute inline-flex size-full animate-ping rounded-full bg-(--color-success) opacity-60"
                        }
                    />
                )}
                <span
                    className={clsx(
                        "relative inline-flex size-2 rounded-full",
                        online ? "bg-(--color-success)" : "bg-zinc-400",
                    )}
                />
            </span>
            <span className={clsx(compact && "max-lg:sr-only")}>{label}</span>
        </Badge>
    );
}
